import { clone } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import type { PlatformFile } from '@healthshare/shared/platform/file';
import type { FileStoreAdapter } from '@healthshare/shared/platform/fileStore';
import { onBlobCreated } from '../../app/reduxToolkit/blobsSlice';
import { AppDispatch } from '../../app/reduxToolkit/store';
import { AttachmentPurpose, BlobInsert, BlobRow } from '../../types/supabase';
import { generateBase36Key } from '../util/generateRandomKey';
import { sha1FromArrayBuffer } from '../util/hashFileSha1';

/**
 * Captures a file locally and queues it to sync. No network required.
 *
 * The bytes are written to the durable local store before anything else happens, so a
 * receipt photographed with no signal is captured immediately. The row rides the ordinary
 * sync queue as a `blobs` add (see syncSlice's SyncActions), and the bytes upload
 * separately -- see SyncActor.uploadPendingFiles() -- whenever the app next has a
 * connection. Reads resolve locally until the upload lands.
 *
 * `file_full_path` is still computed here to satisfy the generated Insert type (the column
 * is NOT NULL with no DB default) and for local optimistic display, but the server always
 * overwrites it with a value derived from membership_id + key -- see
 * SyncApplier::insert()'s blobs branch -- so a stale or incorrect value here is inert.
 * `created_by_user_id` is the same story: SyncApplier derives it from the JWT-authenticated
 * user rather than trusting the client, so the value supplied here is only for local
 * optimistic display before the row syncs back down.
 */
export async function createBlob(
  file: PlatformFile,
  metadata: {
    membership_id: string
    /**
     * For local optimistic display only -- see the function docblock. The row travels
     * through the Laravel sync endpoint, which always overwrites this with the
     * JWT-authenticated user id.
     */
    created_by_user_id?: string | null
    is_receipt_library_eligible?: boolean
    blob_original_purpose?: AttachmentPurpose | null
  },
  dependencies: {
    fileStore: FileStoreAdapter,
    dispatch: AppDispatch
  }
): Promise<BlobRow> {

  const id = uuidv4()
  const key = generateBase36Key()
  // Read once and reuse for the hash and the local store write rather than reading twice.
  const bytes = await file.read()

  await dependencies.fileStore.put(key, bytes, file.type)

  const now = new Date().toISOString()
  const blob: BlobInsert = {
    id,
    key,
    membership_id: metadata.membership_id,
    byte_size: file.size,
    content_type: file.type,
    file_name: file.name,
    file_full_path: `/${metadata.membership_id}/blobs/${key}`,
    sha1: await sha1FromArrayBuffer(bytes),
    created_by_user_id: metadata.created_by_user_id || undefined,
    is_receipt_library_eligible: metadata.is_receipt_library_eligible || false,
    blob_original_purpose: (metadata.blob_original_purpose || undefined),
    created_at: now,
    updated_at: now,
  }

  const blobRow = blob as BlobRow

  // Dispatching onBlobCreated both updates local Redux state and -- via syncSlice's
  // SyncActions mapping -- queues this as an ordinary `blobs` add for the sync engine to
  // push up. No direct PostgREST write happens here anymore.
  dependencies.dispatch(onBlobCreated(clone(blobRow)))

  return blobRow
}
